import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Target, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const REVIEW_STYLES = {
  current: 'bg-emerald-50 text-emerald-600',
  due_soon: 'bg-amber-50 text-amber-600',
  overdue: 'bg-red-50 text-red-600',
  stale: 'bg-orange-50 text-orange-600',
};

const PROGRESS_LABELS = {
  recent: 'Updated recently',
  needs_update: 'Needs progress update',
  no_updates: 'No progress updates',
};

const PROGRESS_STYLES = {
  recent: 'text-emerald-600',
  needs_update: 'text-amber-600',
  no_updates: 'text-muted-foreground',
};

export function SupportSnapshotGoals({ goals, participantId }) {
  const navigate = useNavigate();

  if (!goals) return null;

  const { has_active_goals, active_goals_count, items } = goals;

  return (
    <div data-testid="snapshot-goals-section">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <Target className="w-3 h-3" /> Care Plan Goals
          {active_goals_count > 0 && (
            <Badge variant="secondary" className="text-[9px] ml-1">{active_goals_count}</Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 text-[10px] px-1.5"
          onClick={() => navigate(`/participants/${participantId}?tab=goals`)}
          data-testid="snapshot-view-goals-link"
        >
          View Goals <ArrowRight className="w-3 h-3 ml-0.5" />
        </Button>
      </div>

      {!has_active_goals || !items || items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid="snapshot-goals-empty">No active care plan goals</p>
      ) : (
        <div className="space-y-1.5">
          {items.slice(0, 3).map((g, i) => (
            <div key={g.goal_id || i} className="text-xs px-2.5 py-2 rounded border bg-muted/10" data-testid={`snapshot-goal-${i}`}>
              <div className="flex items-center justify-between gap-1 mb-0.5">
                <span className="font-medium truncate max-w-[200px]">{g.title}</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {g.priority && (
                    <Badge variant="outline" className="text-[8px] px-1 py-0 capitalize">{g.priority}</Badge>
                  )}
                  {g.review_status && (
                    <Badge className={`text-[8px] px-1 py-0 ${REVIEW_STYLES[g.review_status] || ''}`} data-testid={`goal-review-status-${i}`}>
                      {g.review_status === 'due_soon' ? 'Due Soon' : g.review_status}
                    </Badge>
                  )}
                </div>
              </div>
              {g.summary && (
                <p className="text-[10px] text-muted-foreground line-clamp-1">{g.summary}</p>
              )}
              <div className="flex items-center justify-between mt-1">
                {g.progress_percent != null && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary/60 rounded-full" style={{ width: `${Math.min(g.progress_percent, 100)}%` }} />
                    </div>
                    <span className="text-[9px] text-muted-foreground">{g.progress_percent}%</span>
                  </div>
                )}
                <span className={`text-[9px] ${PROGRESS_STYLES[g.progress_status] || 'text-muted-foreground'}`}>
                  {PROGRESS_LABELS[g.progress_status] || ''}
                </span>
              </div>
            </div>
          ))}
          {items.length > 3 && (
            <p className="text-[10px] text-muted-foreground text-center">+{items.length - 3} more goals</p>
          )}
        </div>
      )}
    </div>
  );
}
